/** Wikidata implementations of the linker interfaces (`wbsearchentities` + lexicon). */
import type {
  EntityCandidate,
  EntityLinker,
  LinkOptions,
  PropertyCandidate,
  RelationLinker,
} from "./types.ts";
import { lookupProperty } from "./lexicon.ts";
import { HttpError, withRetry } from "../util/net.ts";

export const WIKIDATA_ENTITY_PREFIX = "http://www.wikidata.org/entity/";
export const WIKIDATA_DIRECT_PREFIX = "http://www.wikidata.org/prop/direct/";
const WIKIDATA_API = "https://www.wikidata.org/w/api.php";

/** Injectable fetch so the server can proxy and tests can stub. */
export type FetchLike = (
  input: string,
  init?: { signal?: AbortSignal },
) => Promise<{ ok: boolean; status: number; json(): Promise<unknown> }>;

// Call `fetch` as a member of the global so `this` is bound correctly — a bare
// `globalThis.fetch` reference throws "Illegal invocation" in browsers.
const defaultFetch: FetchLike = (input, init) => globalThis.fetch(input, init);

interface WbSearchHit {
  id?: string;
  label?: string;
  description?: string;
  match?: { text?: string };
}
interface WbSearchResponse {
  search?: WbSearchHit[];
}

export interface WikidataLinkerOptions {
  fetchImpl?: FetchLike;
  sourceId?: string;
  language?: string;
  /**
   * Relation linker only: also hit the live property-search API even when the
   * curated lexicon already matched. Off by default — the lexicon is
   * authoritative and skipping the extra call halves API pressure. Turn on when
   * you want richer alternatives for guided disambiguation.
   */
  alwaysSearch?: boolean;
}

async function wbSearch(
  fetchImpl: FetchLike,
  query: string,
  type: "item" | "property",
  language: string,
  limit: number,
  signal?: AbortSignal,
): Promise<WbSearchHit[]> {
  const params = new URLSearchParams({
    action: "wbsearchentities",
    search: query,
    language,
    uselang: language,
    format: "json",
    type,
    limit: String(limit),
    // `origin=*` opts the API into permissive CORS so this works unchanged from
    // the browser; it is harmless server-side.
    origin: "*",
  });
  const body = await withRetry(async () => {
    const res = await fetchImpl(`${WIKIDATA_API}?${params.toString()}`, signal ? { signal } : undefined);
    if (!res.ok) throw new HttpError(res.status);
    return (await res.json()) as WbSearchResponse;
  });
  return body.search ?? [];
}

/** Rank-order → score, with an exact-label boost. */
function scoreHit(hit: WbSearchHit, index: number, query: string): number {
  const base = Math.max(0.3, 1 - index * 0.12);
  const label = (hit.label ?? hit.match?.text ?? "").toLowerCase();
  const exact = label === query.trim().toLowerCase() ? 0.15 : 0;
  return Math.min(1, base + exact);
}

export class WikidataEntityLinker implements EntityLinker {
  readonly sourceId: string;
  private readonly fetchImpl: FetchLike;
  private readonly language: string;

  constructor(opts: WikidataLinkerOptions = {}) {
    this.fetchImpl = opts.fetchImpl ?? defaultFetch;
    this.sourceId = opts.sourceId ?? "wikidata";
    this.language = opts.language ?? "en";
  }

  async link(mention: string, opts?: LinkOptions): Promise<EntityCandidate[]> {
    const limit = opts?.limit ?? 7;
    const hits = await wbSearch(this.fetchImpl, mention, "item", this.language, limit, opts?.signal);
    return hits.map((hit, i) => {
      const id = hit.id;
      const candidate: EntityCandidate = {
        iri: id ? WIKIDATA_ENTITY_PREFIX + id : "",
        label: hit.label ?? hit.match?.text ?? id ?? mention,
        score: scoreHit(hit, i, mention),
        sourceId: this.sourceId,
      };
      if (id !== undefined) candidate.id = id;
      if (hit.description !== undefined) candidate.description = hit.description;
      return candidate;
    }).filter((c) => c.iri !== "");
  }
}

export class WikidataRelationLinker implements RelationLinker {
  readonly sourceId: string;
  private readonly fetchImpl: FetchLike;
  private readonly language: string;
  private readonly alwaysSearch: boolean;

  constructor(opts: WikidataLinkerOptions = {}) {
    this.fetchImpl = opts.fetchImpl ?? defaultFetch;
    this.sourceId = opts.sourceId ?? "wikidata";
    this.language = opts.language ?? "en";
    this.alwaysSearch = opts.alwaysSearch ?? false;
  }

  private candidateFromPid(
    pid: string,
    label: string,
    score: number,
    origin: PropertyCandidate["origin"],
    description?: string,
  ): PropertyCandidate {
    const c: PropertyCandidate = {
      iri: WIKIDATA_DIRECT_PREFIX + pid,
      entityIri: WIKIDATA_ENTITY_PREFIX + pid,
      id: pid,
      label,
      score,
      sourceId: this.sourceId,
      origin,
    };
    if (description !== undefined) c.description = description;
    return c;
  }

  async link(phrase: string, opts?: LinkOptions): Promise<PropertyCandidate[]> {
    const candidates: PropertyCandidate[] = [];

    // 1. Curated lexicon first — high precision, no round-trip.
    const lex = lookupProperty(phrase);
    if (lex) candidates.push(this.candidateFromPid(lex.pid, lex.label, 0.98, "lexicon"));

    // 2. Live property search fills in / broadens. Skipped when the lexicon
    // already answered (authoritative) unless `alwaysSearch` asks for more.
    if (lex && !this.alwaysSearch) return candidates;
    try {
      const limit = opts?.limit ?? 5;
      const hits = await wbSearch(this.fetchImpl, phrase, "property", this.language, limit, opts?.signal);
      for (const [i, hit] of hits.entries()) {
        if (!hit.id) continue;
        if (candidates.some((c) => c.id === hit.id)) continue;
        candidates.push(
          this.candidateFromPid(
            hit.id,
            hit.label ?? hit.match?.text ?? hit.id,
            scoreHit(hit, i, phrase) * 0.85,
            "search",
            hit.description,
          ),
        );
      }
    } catch {
      // Search is best-effort; a lexicon hit alone is still a good answer.
    }

    return candidates.sort((a, b) => b.score - a.score);
  }
}
